<template>
  <div class="statement-review">
    <template v-if="isAnalysis">
      <div class="cr-panel" v-for="protocol in visibleProtocols" :key="protocol.key">
        <div class="cr-panel-head" :class="{ 'cr-panel-head-amber': isUnresolved(protocol.key) }">
          <div class="cr-panel-title">
            <fa-icon :icon="['fal', 'layer-group']" class="cr-panel-icon"/>
            Protocolo {{ protocol.numeroProtocolo || protocol.numeroLotePrestador }}
          </div>
          <span class="bg-pill bg-pill-blue" v-if="protocol.situacaoProtocoloName">
            {{ protocol.situacaoProtocolo }} · {{ protocol.situacaoProtocoloName }}
          </span>
          <span class="text-sm" v-if="protocol.batch">
            <fa-icon :icon="['fal', 'arrow-right']" class="text-gray"/>
            <b class="text-green">Lote {{ protocol.batch.number }}</b>
            <span class="text-xs text-gray">{{ batchMeta(protocol) }}</span>
          </span>
          <template v-else-if="entryOf(protocol.key)">
            <span class="bg-pill bg-pill-amber" v-if="!entryOf(protocol.key).ignored">
              <fa-icon :icon="['fal', 'exclamation-triangle']"/>
                {{ reasonText(entryOf(protocol.key)) }}
            </span>
            <span class="bg-pill bg-pill-gray" v-else>Ignorado</span>
            <span class="bg-resolver" v-if="!readonly && !entryOf(protocol.key).ignored">
              <select class="form-select select-sm" v-if="candidatesOf(protocol.key).length"
                      @change="resolveBatch(protocol.key, $event.target.value)">
                <option value="">Escolher lote…</option>
                <option v-for="candidate in entryOf(protocol.key).candidates" :value="candidate.id"
                        :key="candidate.id">
                  Lote {{ candidate.number }} · {{ batchStatusName(candidate.status) }}
                  · {{ candidate.totals && $options.filters.currency(candidate.totals.informed) }}
                </option>
              </select>
              <dx-autocomplete v-else
                               :source="findBatch"
                               label="number"
                               track-by="id"
                               :custom-label="batchLabel"
                               :debounce="500"
                               :min-len="1"
                               placeholder="Escolher lote (nº ou protocolo)…"
                               @select="(batch) => batch && resolveBatch(protocol.key, batch.id)"/>
              <button class="btn btn-link btn-sm" @click="$emit('ignore', protocol.key)">
                Ignorar este protocolo
              </button>
            </span>
            <button class="btn btn-link btn-sm" v-if="!readonly && entryOf(protocol.key).ignored"
                    @click="$emit('unignore', protocol.key)">Voltar a conciliar</button>
          </template>
          <div class="cr-panel-right text-xs text-gray tnum">
            informado {{ protocol.totals.informed | currency }}
            · liberado {{ protocol.totals.released | currency }}
            · glosa {{ protocol.totals.glossed | currency }}
          </div>
          <div class="bg-note bg-note-info bg-note-inline" v-if="protocol.gloss">
            Glosa no nível do protocolo:
            <gloss-code-pill :code="protocol.gloss.code" :description="protocol.gloss.description"
                             :value="protocol.gloss.value"/>
            rateada em todas as guias do lote.
          </div>
        </div>

        <template v-for="guide in visibleGuides(protocol)">
          <div class="bg-guide-row" :key="guide.key"
               :class="{ 'bg-guide-row-amber': isUnresolved(guide.key) }">
            <span :class="guideIconClass(protocol, guide)">
              <fa-icon :icon="['fal', 'circle']"/>
            </span>
            <div>
              <span class="bg-guide-n">Guia {{ guide.numeroGuiaPrestador }}</span>
              <span class="text-xs text-gray" v-if="guide.numeroGuiaOperadora">
                · operadora {{ guide.numeroGuiaOperadora }}
              </span>
              <span class="bg-guide-m">
                {{ guide.account && guide.account.patient
                  ? guide.account.patient.name
                  : 'Beneficiário' }}
                <template v-if="guide.numeroCarteira">
                  · carteira {{ guide.numeroCarteira }}
                </template>
              </span>
            </div>
            <div class="text-sm">
              <template v-if="guide.account">
                <span class="text-green">
                  <fa-icon :icon="['fal', 'check']"/>
                    Guia {{ guide.account.referral && guide.account.referral.provider }} casada
                </span>
                <span class="bg-guide-m">{{ guideItemsSummary(guide) }}</span>
              </template>
              <template v-else-if="!protocol.batch">
                <span class="text-gray">aguardando o lote</span>
              </template>
              <template v-else-if="entryOf(guide.key) && entryOf(guide.key).ignored">
                <span class="text-gray">ignorada</span>
                <button class="btn btn-link btn-sm" v-if="!readonly"
                        @click="$emit('unignore', guide.key)">
                  Voltar a conciliar
                </button>
              </template>
              <template v-else>
                <span class="text-amber">
                  <fa-icon :icon="['fal', 'exclamation-triangle']"/>
                  Guia {{ guide.numeroGuiaPrestador }} não encontrada no lote
                  {{ protocol.batch.number }}
                </span>
                <span class="bg-guide-m">
                  talvez esteja em outro lote ou o nº foi digitado diferente
                </span>
              </template>
            </div>
            <div>
              <span class="bg-pill bg-pill-outline" v-if="guide.situacaoGuia">
                Situação: {{ guide.situacaoGuia }}
              </span>
            </div>
            <template v-if="!(protocol.batch && !guide.account && !(entryOf(guide.key) &&
            entryOf(guide.key).ignored))">
              <div class="text-right tnum hide-sm">{{ guide.totals.informed | currency }}</div>
              <div class="text-right tnum hide-sm">{{ guide.totals.processed | currency }}</div>
              <div class="text-right tnum hide-sm">{{ guide.totals.released | currency }}</div>
              <div class="text-right tnum text-red hide-sm">
                −{{ guide.totals.glossed | currency }}
              </div>
              <div class="text-right">
                <button class="btn btn-link btn-sm" v-if="plainItems(guide).length > 0"
                        @click="toggleExpanded(guide.key)">
                  {{ expanded[guide.key] ? '− ocultar' : '+' }} {{ plainItems(guide).length }}
                  {{ plainItems(guide).length === 1 ? 'item sem glosa' : 'itens sem glosa' }}
                </button>
              </div>
            </template>
            <div class="bg-guide-resolve" v-else>
              <template v-if="!readonly">
                <select class="form-select select-sm bg-resolver-select"
                        @change="resolveGuide(guide.key, $event.target.value)">
                  <option value="">Escolher guia (nº, paciente)…</option>
                  <option v-for="candidate in candidatesOf(guide.key)" :value="candidate.id"
                          :key="candidate.id">
                    {{ candidate.referral && candidate.referral.provider }}
                    · {{ candidate.patient && candidate.patient.name }}
                    · {{ candidate.totals && $options.filters.currency(candidate.totals.informed) }}
                  </option>
                </select>
                <button class="btn btn-link btn-sm" @click="$emit('ignore', guide.key)">
                  Ignorar esta guia
                </button>
              </template>
              <span class="text-xs text-gray" v-else>não conciliada</span>
            </div>
          </div>
          <div class="bg-guide-items" :key="`${guide.key}-items`"
               v-if="guide.glosses.length > 0 || visibleItems(guide).length > 0">
            <div class="bg-note bg-note-info" v-if="guide.glosses.length > 0">
              Glosa no nível da guia:
              <gloss-code-pill v-for="(gloss, index) in guide.glosses" :key="index"
                               :code="gloss.code" :description="gloss.description"
                               :value="gloss.value"/>
              — será rateada nos {{ guide.items.length }}
              itens proporcionalmente ao valor informado.
            </div>
            <table class="table" v-if="visibleItems(guide).length > 0">
              <thead>
              <tr>
                <th>Seq</th>
                <th>Procedimento</th>
                <th class="text-right">Qtd</th>
                <th class="text-right">Informado</th>
                <th class="text-right">Processado</th>
                <th class="text-right">Liberado</th>
                <th>Glosa</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="line in visibleItems(guide)" :key="line.key"
                  :class="{ 'bg-row-amber': isUnresolved(line.key) }">
                <td><span class="bg-seq">{{ line.sequencialItem }}</span></td>
                <td class="bg-proc">
                  <code>
                    {{ line.code }}</code><b>
                    {{ line.description
                      || (line.item && line.item.expense && line.item.expense.name) }}
                  </b>
                  <small v-if="line.divergence">
                    No Stenci: {{ line.divergence.informed.system | currency }} · qtd
                    {{ line.divergence.quantity.system }}
                  </small>
                  <small class="text-amber"
                         v-else-if="guide.account && !line.item && !isIgnored(line.key)">
                    Item seq. {{ line.sequencialItem }} não existe na guia escolhida
                  </small>
                  <small class="text-gray" v-else-if="isIgnored(line.key)">item ignorado</small>
                </td>
                <td class="text-right">{{ line.quantity }}</td>
                <td class="text-right tnum" :class="{ 'bg-diverge tooltip tooltip-left':
                line.divergence }"
                    :data-tooltip="line.divergence ? divergenceTooltip(line) : null">
                  {{ line.informed | currency }}
                  <fa-icon v-if="line.divergence" :icon="['fal', 'times']"/>
                </td>
                <td class="text-right tnum">{{ line.processed | currency }}</td>
                <td class="text-right tnum">{{ line.released | currency }}</td>
                <td>
                  <div class="bg-resolver"
                       v-if="guide.account && !line.item && !isIgnored(line.key) && !readonly">
                    <select class="form-select select-sm bg-resolver-select"
                            @change="resolveItem(line.key, $event.target.value)">
                      <option value="">Escolher item da guia…</option>
                      <option v-for="candidate in candidatesOf(line.key)" :value="candidate.id"
                              :key="candidate.id">
                        {{ candidate.sequencialItem }} ·
                        {{ candidate.expense && candidate.expense.code }}
                        {{ candidate.expense && candidate.expense.name }}
                        ·
                        {{ candidate.value && $options.filters.currency(candidate.value.insurance)
                        }}
                      </option>
                    </select>
                    <button class="btn btn-link btn-sm" @click="$emit('ignore', line.key)">
                      Ignorar
                    </button>
                  </div>
                  <div class="bg-gstack" v-else-if="line.glosses.length > 0 || line.divergence">
                    <gloss-code-pill v-for="(gloss, index) in line.glosses" :key="index"
                                     :code="gloss.code" :description="gloss.description"
                                     :value="gloss.value"/>
                    <small v-if="line.divergence && line.divergence.conciliationGloss > 0">
                      + conciliação <span class="bg-gcode"><span
                      class="bg-gcode-code">9999</span></span>
                      diferença de valor −{{ line.divergence.conciliationGloss | currency }}
                    </small>
                  </div>
                  <span class="text-xs text-gray" v-else>sem glosa</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </template>
      </div>
    </template>

    <template v-else>
      <div class="cr-panel" v-for="(payment, index) in payments" :key="index">
        <div class="cr-panel-head">
          <div class="cr-panel-title">
            <fa-icon :icon="['fal', 'hand-holding-usd']" class="cr-panel-icon"/>
            Pagamento em {{ payment.date | date }}
          </div>
          <span class="bg-pill bg-pill-outline" v-if="payment.form">{{ payment.form }}</span>
          <span class="text-sm text-gray" v-if="payment.bank || payment.account">
            Banco {{ payment.bank }} · ag {{ payment.agency }} · cc {{ payment.account }}
          </span>
        </div>
        <div class="cr-table-scroll">
          <table class="table">
            <thead>
            <tr>
              <th>Protocolo</th>
              <th>Lote</th>
              <th>Tipo</th>
              <th class="text-right">Bruto</th>
              <th class="text-right">Glosado</th>
              <th class="text-right">Pago</th>
              <th>Conciliação</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="protocol in payment.protocols" :key="protocol.key"
                :class="{ 'bg-row-amber': isUnresolved(protocol.key) }">
              <td>{{ protocol.numeroProtocolo || '—' }}</td>
              <td>
                <template v-if="protocol.batch">
                  <b>Lote {{ protocol.batch.number }}</b>
                  <span class="text-gray" v-if="protocol.numeroLote">
                    · nº {{ protocol.numeroLote }}
                  </span>
                </template>
                <div class="bg-resolver"
                     v-else-if="!readonly && entryOf(protocol.key) &&
                     !entryOf(protocol.key).ignored">
                  <select class="form-select select-sm" v-if="candidatesOf(protocol.key).length"
                          @change="resolveBatch(protocol.key, $event.target.value)">
                    <option value="">Escolher lote…</option>
                    <option v-for="candidate in candidatesOf(protocol.key)" :value="candidate.id"
                            :key="candidate.id">
                      Lote {{ candidate.number }} · {{ batchStatusName(candidate.status) }}
                    </option>
                  </select>
                  <dx-autocomplete v-else
                                   :source="findBatch"
                                   label="number"
                                   track-by="id"
                                   :custom-label="batchLabel"
                                   :debounce="500"
                                   :min-len="1"
                                   placeholder="Escolher lote…"
                                   @select="(batch) => batch && resolveBatch(protocol.key,
                                   batch.id)"/>
                  <button class="btn btn-link btn-sm" @click="$emit('ignore', protocol.key)">
                    Ignorar
                  </button>
                </div>
                <span class="text-gray" v-else>
                  {{ isIgnored(protocol.key) ? 'ignorado' : 'lote não encontrado' }}
                  <button class="btn btn-link btn-sm" v-if="!readonly && isIgnored(protocol.key)"
                          @click="$emit('unignore', protocol.key)">Voltar a conciliar</button>
                </span>
              </td>
              <td>
                <span class="bg-pill"
                      :class="String(protocol.tipoPagamento) === '2' ? 'bg-pill-green' :
                      'bg-pill-outline'">
                  {{ protocol.tipoPagamento || 1 }} · {{ paymentTypeName(protocol.tipoPagamento) }}
                </span>
              </td>
              <td class="text-right tnum">{{ protocol.gross | currency }}</td>
              <td class="text-right tnum text-red">−{{ protocol.glossed | currency }}</td>
              <td class="text-right tnum"><b>{{ protocol.paid | currency }}</b></td>
              <td>
                <template v-if="receivableOf(protocol)">
                  <span class="text-green" v-if="receivableOf(protocol).status === 'open'">
                    <fa-icon :icon="['fal', 'check']"/>
                    título {{ receivableOf(protocol).document }} · aberto ·
                    {{ receivableOf(protocol).value | currency }}
                  </span>
                  <span class="text-gray" v-else-if="receivableOf(protocol).alreadyPaid">
                    título {{ receivableOf(protocol).document }} · já baixado
                  </span>
                  <span class="text-amber" v-else-if="receivableOf(protocol).missing">
                    <fa-icon :icon="['fal', 'exclamation-triangle']"/> sem título correspondente
                  </span>
                </template>
                <div class="text-amber" v-if="appealResponseOf(protocol)">
                  <fa-icon :icon="['fal', 'exclamation-triangle']"/>
                  recurso {{ appealResponseOf(protocol).number }} sem resposta registrada
                  <button class="btn btn-link btn-sm" v-if="!readonly"
                          @click="$emit('respond', appealResponseOf(protocol))">Registrar
                          resposta</button>
                </div>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <div class="cr-panel-head cr-panel-head-inner"
             v-if="payment.debits && payment.debits.length">
          <div class="cr-panel-title cr-panel-title-sm">Débitos e créditos nesta data</div>
        </div>
        <table class="table" v-if="payment.debits && payment.debits.length">
          <tbody>
          <tr v-for="(debit, debitIndex) in payment.debits" :key="debitIndex">
            <td class="bg-debit-type">
              <span class="bg-pill" :class="isDebit(debit) ? 'bg-pill-outline' : 'bg-pill-green'">
                {{ isDebit(debit) ? 'Débito' : 'Crédito' }} {{ debit.type }}
                <template v-if="debitName(debit)">· {{ debitName(debit) }}</template>
              </span>
            </td>
            <td>{{ debit.description || debitName(debit) || '—' }}</td>
            <td class="text-right tnum" :class="isDebit(debit) ? 'text-red' : 'text-green'">
              {{ isDebit(debit) ? '−' : '+' }}{{ debit.value | currency }}
            </td>
          </tr>
          </tbody>
        </table>
        <div class="cr-panel-foot">
          <span>Bruto <b>{{ paymentTotals(payment).gross | currency }}</b></span>
          <span>
            Débitos <b class="text-red">−{{ paymentTotals(payment).debits | currency }}</b>
          </span>
          <span v-if="paymentTotals(payment).credits">
            Créditos <b class="text-green">+{{ paymentTotals(payment).credits | currency }}</b>
          </span>
          <span>
            Líquido <b class="text-primary">{{ paymentTotals(payment).net | currency }}</b>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getPaymentTypeName } from '@/data/billing-statement-types';
import { getName as debitTypeName, DEBIT_INDICATOR } from '@/data/tiss-payment-debit-types';
import GlossCodePill from './GlossCodePill.vue';
import { batchStatuses, guideTypes, idOf } from '../helpers';

const REASONS = {
  batchNotFound: 'Lote não encontrado',
  batchNotSent: 'Lote ainda não enviado',
  batchFinal: 'Lote baixado ou cancelado',
  guideNotFound: 'Guia não encontrada',
  itemNotFound: 'Item não encontrado',
  ignored: 'Ignorado',
};

export default {
  components: {
    GlossCodePill,
  },
  props: {
    preview: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: String,
      default: 'all',
    },
    insuranceId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      expanded: {},
    };
  },
  computed: {
    isAnalysis() {
      return this.preview.statement.type === 'analysis';
    },
    matching() {
      return this.preview.matching || {};
    },
    protocols() {
      return this.matching.protocols || [];
    },
    payments() {
      return this.matching.payments || [];
    },
    entries() {
      return new Map((this.matching.unmatched || []).map(entry => [entry.key, entry]));
    },
    visibleProtocols() {
      if (this.filter === 'all') return this.protocols;
      return this.protocols.filter(protocol => this.visibleGuides(protocol).length > 0
        || (this.filter === 'unmatched' && this.isUnresolved(protocol.key)));
    },
  },
  methods: {
    paymentTotals(payment) {
      const totals = payment.totals || {};
      return {
        gross: totals.gross !== undefined ? totals.gross : payment.gross || 0,
        debits: totals.debits !== undefined ? totals.debits : payment.debitsTotal || 0,
        credits: totals.credits !== undefined ? totals.credits : payment.creditsTotal || 0,
        net: totals.net !== undefined ? totals.net : payment.net || 0,
      };
    },
    entryOf(key) {
      return this.entries.get(key) || null;
    },
    isUnresolved(key) {
      const entry = this.entryOf(key);
      return !!entry && !entry.ignored;
    },
    isIgnored(key) {
      const entry = this.entryOf(key);
      return !!entry && entry.ignored;
    },
    candidatesOf(key) {
      const entry = this.entryOf(key);
      return (entry && entry.candidates) || [];
    },
    reasonText(entry) {
      return REASONS[entry.reason] || entry.reason;
    },
    batchStatusName(status) {
      return batchStatuses[status] || status;
    },
    batchMeta(protocol) {
      const parts = [];
      if (protocol.matchedBy === 'manual') parts.push('escolhido manualmente');
      else parts.push('encontrado');
      if (protocol.batch.guideType) {
        parts.push(guideTypes[protocol.batch.guideType] || protocol.batch.guideType);
      }
      if (protocol.batch.status) {
        parts.push(this.batchStatusName(protocol.batch.status).toLowerCase());
      }
      return parts.join(' · ');
    },
    batchLabel(batch) {
      return batch ? `Lote ${batch.number}` : '';
    },
    findBatch(search) {
      if (!this.insuranceId) return Promise.resolve([]);
      return this.$http.get('/billing-batches', {
        params: { insuranceId: this.insuranceId, search: String(search || '').trim(), limit: 20 },
      })
        .then(({ data }) => (data.items || []).map(batch => ({ ...batch, id: idOf(batch) })))
        .catch(() => []);
    },
    resolveBatch(key, batchId) {
      if (batchId) this.$emit('resolve', { key, batchId });
    },
    resolveGuide(key, accountId) {
      if (accountId) this.$emit('resolve', { key, accountId });
    },
    resolveItem(key, itemId) {
      if (itemId) this.$emit('resolve', { key, itemId });
    },
    guideIconClass(protocol, guide) {
      if (this.isUnresolved(guide.key) || (protocol.batch && !guide.account && !this.isIgnored(guide.key))) return 'bg-ic-warn';
      if (guide.items.some(line => line.divergence || this.isUnresolved(line.key))) return 'bg-ic-err';
      if (guide.account) return 'bg-ic-ok';
      return 'text-gray';
    },
    guideItemsSummary(guide) {
      const glossed = guide.items.filter(line => line.glosses.length > 0).length;
      const divergences = guide.items.filter(line => line.divergence).length;
      const parts = [`${guide.items.length} ${guide.items.length === 1 ? 'item' : 'itens'}`];
      if (guide.glosses.length > 0) parts.push('glosa da guia inteira');
      else if (glossed) parts.push(`${glossed} com glosa`);
      if (divergences) parts.push(`${divergences} ${divergences === 1 ? 'divergência' : 'divergências'} de valor`);
      return parts.join(' · ');
    },
    plainItems(guide) {
      return guide.items.filter(line => line.glosses.length === 0 && !line.divergence
        && !this.isUnresolved(line.key) && !(guide.account && !line.item));
    },
    visibleItems(guide) {
      return guide.items.filter((line) => {
        const flagged = line.glosses.length > 0 || line.divergence || this.isUnresolved(line.key)
          || (guide.account && !line.item);
        if (this.filter === 'diverge') return !!line.divergence;
        if (this.filter === 'unmatched') return this.isUnresolved(line.key) || (guide.account && !line.item && !this.isIgnored(line.key));
        return flagged || this.expanded[guide.key];
      });
    },
    visibleGuides(protocol) {
      return protocol.guides.filter((guide) => {
        if (this.filter === 'all') return true;
        if (this.filter === 'unmatched') {
          return this.isUnresolved(guide.key)
            || (protocol.batch && !guide.account && !this.isIgnored(guide.key))
            || guide.items.some(line => this.isUnresolved(line.key));
        }
        return guide.items.some(line => line.divergence);
      });
    },
    toggleExpanded(key) {
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    divergenceTooltip(line) {
      const system = this.$options.filters.currency(line.divergence.informed.system);
      const diff = line.divergence.conciliationGloss;
      if (diff > 0) {
        return `No Stenci: ${system}. O valor do arquivo prevalece; a diferença de ${this.$options.filters.currency(diff)} vira glosa de conciliação (9999).`;
      }
      return `No Stenci: ${system} · qtd ${line.divergence.quantity.system}. O valor do arquivo prevalece na análise.`;
    },
    paymentTypeName(type) {
      return getPaymentTypeName(type);
    },
    isDebit(debit) {
      return String(debit.indicator || DEBIT_INDICATOR) === DEBIT_INDICATOR;
    },
    debitName(debit) {
      return debitTypeName(debit.type);
    },
    receivableOf(protocol) {
      if (!protocol.batch || !this.preview.payments) return null;
      return (this.preview.payments.receivables || []).find(line => (
        String(line.batchId) === idOf(protocol.batch)
        && String(line.tipoPagamento || '1') === String(protocol.tipoPagamento || '1')
      )) || null;
    },
    appealResponseOf(protocol) {
      if (!protocol.batch || !this.preview.payments) return null;
      return (this.preview.payments.appealResponses || []).find(response => (
        String(response.batchId) === idOf(protocol.batch)
      )) || null;
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";

.statement-review {
  .bg-resolver {
    align-items: center;
    display: inline-flex;
    flex-wrap: wrap;
    gap: $layout-spacing-sm;
    .form-select, .dx-autocomplete, .autocomplete { min-width: 11rem; }
  }
  .bg-resolver-select { max-width: 16rem; }
  .bg-note-inline { flex-basis: 100%; margin: $layout-spacing-sm 0 0; }
  .bg-debit-type { white-space: nowrap; width: 8rem; }
}
</style>
